// ChangeStatusModal.tsx - Çek/Senet Durum Değiştirme Modalı
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
  ListSubheader,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoneyIcon from '@mui/icons-material/Money';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import SendIcon from '@mui/icons-material/Send';
import SecurityIcon from '@mui/icons-material/Security';
import PanToolIcon from '@mui/icons-material/PanTool';
import {
  CheckNoteDto,
  ChangeCheckNoteStatusDto,
  CheckNoteStatus,
  CheckNoteStatusValue,
  CheckNoteType,
  CheckNoteTypeValue,
  getStatusColor,
  formatCurrency,
  changeCheckNoteStatus
} from '../../../services/checkNoteService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';
import {
  CollectionMethod,
  getDefaultCollectionMethod,
  getSentBankOptionId,
  parseBankOptionId,
  shouldWarnNonBankCollection
} from './collectionDefaults';

interface ChangeStatusModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  checkNote: CheckNoteDto | null;
  companyId: string;
}

interface Account {
  id: number;
  name: string;
  code: string;
}

interface BankAccount {
  id: number | string; // Alt hesaplar için "parentId_subId" formatı
  bankName: string;
  accountName: string;
  iban?: string;
  isSubAccount: boolean;
  parentBankAccountId?: number;
  subAccountId?: number;
}

interface Cashbox {
  id: number;
  name: string;
  balance?: number;
}

// Senetler için geçerli olmayan durumlar (sadece çeklere özgü)
const CHECK_ONLY_STATUSES: CheckNoteStatusValue[] = [
  CheckNoteStatus.BOUNCED,           // Karşılıksız - sadece çeklerde var (banka üzerinden)
  CheckNoteStatus.PARTIAL_COLLECTED, // Kısmi tahsilat - senetlerde genellikle ya tam ödenir ya ödenmez
  CheckNoteStatus.IN_COLLECTION      // Tahsilde - çekler bankaya verilir, senetler değil
  // NOT: ENDORSED (Ciro) senetler için de geçerlidir - TTK Madde 690
];

// Mevcut durumdan geçilebilecek durumları tanımla
const getAvailableStatuses = (currentStatus: CheckNoteStatusValue, noteType?: CheckNoteTypeValue): CheckNoteStatusValue[] => {
  const transitions: { [key: string]: CheckNoteStatusValue[] } = {
    // Alınan çekler için geçişler
    [CheckNoteStatus.PORTFOLIO]: [
      CheckNoteStatus.IN_COLLECTION,
      CheckNoteStatus.COLLECTED,
      CheckNoteStatus.ENDORSED,
      CheckNoteStatus.COLLATERAL,
      CheckNoteStatus.RETURNED,
      CheckNoteStatus.CANCELED
    ],
    [CheckNoteStatus.IN_COLLECTION]: [
      CheckNoteStatus.COLLECTED,
      CheckNoteStatus.PARTIAL_COLLECTED,
      CheckNoteStatus.BOUNCED,
      CheckNoteStatus.RETURNED,
      CheckNoteStatus.PORTFOLIO
    ],
    [CheckNoteStatus.PARTIAL_COLLECTED]: [
      CheckNoteStatus.COLLECTED,
      CheckNoteStatus.PARTIAL_COLLECTED, // Birden fazla kısmi tahsil yapılabilir
      CheckNoteStatus.BOUNCED
    ],
    [CheckNoteStatus.COLLATERAL]: [
      CheckNoteStatus.COLLATERAL_RETURNED,
      CheckNoteStatus.COLLECTED
    ],
    [CheckNoteStatus.COLLATERAL_RETURNED]: [
      CheckNoteStatus.PORTFOLIO,
      CheckNoteStatus.IN_COLLECTION
    ],
    [CheckNoteStatus.BOUNCED]: [
      CheckNoteStatus.COLLECTED,
      CheckNoteStatus.RETURNED
    ],
    [CheckNoteStatus.ENDORSED]: [],
    [CheckNoteStatus.COLLECTED]: [],
    [CheckNoteStatus.RETURNED]: [],
    [CheckNoteStatus.CANCELED]: [],
    // Verilen çekler için geçişler
    [CheckNoteStatus.GIVEN_PENDING]: [
      CheckNoteStatus.GIVEN_PAID,
      CheckNoteStatus.GIVEN_RETURNED,
      CheckNoteStatus.BOUNCED,
      CheckNoteStatus.CANCELED
    ],
    [CheckNoteStatus.GIVEN_PAID]: [],
    [CheckNoteStatus.GIVEN_RETURNED]: [
      CheckNoteStatus.GIVEN_PENDING
    ]
  };
  
  let statuses = transitions[currentStatus] || [];
  
  // Senet ise çeklere özgü durumları filtrele
  if (noteType === CheckNoteType.PROMISSORY_NOTE) {
    statuses = statuses.filter(status => !CHECK_ONLY_STATUSES.includes(status));
  }
  
  return statuses;
};

// Durum açıklamaları
const getStatusDescription = (status: CheckNoteStatusValue, t: any): string => {
  const descriptions: { [key: string]: string } = {
    [CheckNoteStatus.IN_COLLECTION]: t('checksNotes.statusDescriptions.inCollection'),
    [CheckNoteStatus.COLLECTED]: t('checksNotes.statusDescriptions.collected'),
    [CheckNoteStatus.PARTIAL_COLLECTED]: t('checksNotes.statusDescriptions.partialCollected'),
    [CheckNoteStatus.ENDORSED]: t('checksNotes.statusDescriptions.endorsed'),
    [CheckNoteStatus.COLLATERAL]: t('checksNotes.statusDescriptions.collateral'),
    [CheckNoteStatus.COLLATERAL_RETURNED]: t('checksNotes.statusDescriptions.collateralReturned'),
    [CheckNoteStatus.BOUNCED]: t('checksNotes.statusDescriptions.bounced'),
    [CheckNoteStatus.RETURNED]: t('checksNotes.statusDescriptions.returned'),
    [CheckNoteStatus.CANCELED]: t('checksNotes.statusDescriptions.canceled'),
    [CheckNoteStatus.PORTFOLIO]: t('checksNotes.statusDescriptions.portfolio'),
    // Verilen çekler için açıklamalar
    [CheckNoteStatus.GIVEN_PENDING]: t('checksNotes.statusDescriptions.givenPending'),
    [CheckNoteStatus.GIVEN_PAID]: t('checksNotes.statusDescriptions.givenPaid'),
    [CheckNoteStatus.GIVEN_RETURNED]: t('checksNotes.statusDescriptions.givenReturned')
  };
  
  return descriptions[status] || '';
};

// Durum için ikon
const getStatusIcon = (status: CheckNoteStatusValue) => {
  const icons: { [key: string]: React.ReactNode } = {
    [CheckNoteStatus.IN_COLLECTION]: <AccountBalanceIcon />,
    [CheckNoteStatus.COLLECTED]: <CheckIcon />,
    [CheckNoteStatus.PARTIAL_COLLECTED]: <MoneyIcon />,
    [CheckNoteStatus.ENDORSED]: <SendIcon />,
    [CheckNoteStatus.COLLATERAL]: <SecurityIcon />,
    [CheckNoteStatus.COLLATERAL_RETURNED]: <ReplayIcon />,
    [CheckNoteStatus.BOUNCED]: <WarningAmberIcon />,
    [CheckNoteStatus.RETURNED]: <ReplayIcon />,
    [CheckNoteStatus.CANCELED]: <CloseIcon />,
    [CheckNoteStatus.PORTFOLIO]: <AccountBalanceIcon />,
    // Verilen çekler için ikonlar
    [CheckNoteStatus.GIVEN_PENDING]: <MoneyIcon />,
    [CheckNoteStatus.GIVEN_PAID]: <CheckIcon />,
    [CheckNoteStatus.GIVEN_RETURNED]: <ReplayIcon />
  };
  
  return icons[status] || <SwapHorizIcon />;
};

const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  open,
  onClose,
  onSuccess,
  checkNote,
  companyId
}) => {
  const { t } = useTranslation('common2');
  
  const getLocalizedStatusLabel = (status: CheckNoteStatusValue): string => {
    const keyMap: Record<CheckNoteStatusValue, string> = {
      [CheckNoteStatus.PORTFOLIO]: 'portfolio',
      [CheckNoteStatus.IN_COLLECTION]: 'inCollection',
      [CheckNoteStatus.COLLECTED]: 'collected',
      [CheckNoteStatus.PARTIAL_COLLECTED]: 'partialCollected',
      [CheckNoteStatus.ENDORSED]: 'endorsed',
      [CheckNoteStatus.COLLATERAL]: 'collateral',
      [CheckNoteStatus.COLLATERAL_RETURNED]: 'collateralReturned',
      [CheckNoteStatus.BOUNCED]: 'bounced',
      [CheckNoteStatus.RETURNED]: 'returned',
      [CheckNoteStatus.CANCELED]: 'canceled',
      [CheckNoteStatus.GIVEN_PENDING]: 'givenPending',
      [CheckNoteStatus.GIVEN_PAID]: 'givenPaid',
      [CheckNoteStatus.GIVEN_RETURNED]: 'givenReturned',
    };
    return t(`checksNotes.statuses.${keyMap[status]}`);
  };
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [loadingCashboxes, setLoadingCashboxes] = useState(false);
  
  // Form state
  const [selectedStatus, setSelectedStatus] = useState<CheckNoteStatusValue | ''>('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [endorsedToAccountId, setEndorsedToAccountId] = useState<number | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | string | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [selectedCashboxId, setSelectedCashboxId] = useState<number | null>(null);
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  // Tahsilat yöntemi (yalnızca COLLECTED / PARTIAL_COLLECTED için): bankaya gönderilmiş çekte Banka, diğerlerinde Kasa
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>('CASHBOX');
  
  // Mevcut durumdan geçilebilecek durumlar (senet için karşılıksız ve kısmi tahsilat gizlenir)
  const availableStatuses = checkNote ? getAvailableStatuses(checkNote.status, checkNote.type) : [];

  // Tahsilat durumu (kasa/banka/elden seçimi sunulur)
  const isCollectionStatus = selectedStatus === CheckNoteStatus.COLLECTED ||
                             selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED;
  
  // Cari hesapları yükle (ciro için)
  useEffect(() => {
    const loadAccounts = async () => {
      if (!companyId) return;
      
      setLoadingAccounts(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        // API yanıtı { success, data } formatında veya doğrudan array olabilir
        const accountsData = response.data?.data || response.data?.Data || response.data;
        if (accountsData && Array.isArray(accountsData)) {
          setAccounts(accountsData.map((acc: any) => ({
            id: acc.Id || acc.id,
            name: acc.CompanyName || acc.companyName || acc.Name || acc.name,
            code: acc.AccountCode || acc.accountCode || acc.Code || acc.code || ''
          })));
        }
      } catch (err) {
        console.error('Cari hesaplar yüklenemedi:', err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    
    if (open && selectedStatus === CheckNoteStatus.ENDORSED) {
      loadAccounts();
    }
  }, [open, companyId, selectedStatus]);
  
  // Banka hesaplarını yükle (teminat, tahsilde, verilen çek ödeme için)
  const needsBankAccount = selectedStatus === CheckNoteStatus.COLLATERAL || 
                           selectedStatus === CheckNoteStatus.IN_COLLECTION ||
                           selectedStatus === CheckNoteStatus.COLLECTED ||
                           selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED ||
                           selectedStatus === CheckNoteStatus.GIVEN_PAID;
  
  useEffect(() => {
    const loadBankAccounts = async () => {
      if (!companyId) return;
      
      setLoadingBankAccounts(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
        const data = response.data?.data || response.data?.Data || response.data;
        if (data && Array.isArray(data)) {
          const options: BankAccount[] = [];
          
          data.forEach((bank: any) => {
            const bankId = bank.Id || bank.id;
            const bankName = bank.BankName || bank.bankName || '';
            const accountName = bank.AccountName || bank.accountName || bank.Name || bank.name || '';
            const iban = bank.Iban || bank.iban || bank.IBAN || '';
            
            // Ana hesabı ekle
            options.push({
              id: bankId,
              bankName,
              accountName,
              iban,
              isSubAccount: false
            });
            
            // Alt hesapları ekle
            const subAccounts = bank.SubAccounts || bank.subAccounts;
            if (subAccounts) {
              // Object veya Array formatını destekle
              const subList = Array.isArray(subAccounts) ? subAccounts : Object.values(subAccounts);
              subList.forEach((sub: any) => {
                const subId = sub.Id || sub.id;
                const subName = sub.AccountName || sub.accountName || sub.Name || sub.name || '';
                const subIban = sub.Iban || sub.iban || sub.IBAN || iban;
                
                options.push({
                  id: `${bankId}_${subId}`,
                  bankName,
                  accountName: subName,
                  iban: subIban,
                  isSubAccount: true,
                  parentBankAccountId: bankId,
                  subAccountId: subId
                });
              });
            }
          });
          
          setBankAccounts(options);
        }
      } catch (err) {
        console.error('Banka hesapları yüklenemedi:', err);
      } finally {
        setLoadingBankAccounts(false);
      }
    };
    
    if (open && needsBankAccount) {
      loadBankAccounts();
    }
  }, [open, companyId, needsBankAccount]);
  
  // Kasaları yükle (tahsilat — nakit/kasa seçeneği için)
  useEffect(() => {
    const loadCashboxes = async () => {
      if (!companyId) return;
      setLoadingCashboxes(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
        const data = response.data?.data || response.data?.Data || response.data;
        if (data && Array.isArray(data)) {
          const list: Cashbox[] = data
            .filter((c: any) => (c.isActive ?? c.IsActive) !== false)
            .map((c: any) => ({
              id: c.id ?? c.Id,
              name: c.name ?? c.Name ?? '',
              balance: c.balance ?? c.Balance ?? 0,
            }));
          setCashboxes(list);
        }
      } catch (err) {
        console.error('Kasalar yüklenemedi:', err);
      } finally {
        setLoadingCashboxes(false);
      }
    };
    
    if (open && isCollectionStatus) {
      loadCashboxes();
    }
  }, [open, companyId, isCollectionStatus]);
  
  // Modal açıldığında formu sıfırla
  useEffect(() => {
    if (open) {
      setSelectedStatus('');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setAmount(checkNote?.remainingAmount || 0);
      setDescription('');
      setEndorsedToAccountId(null);
      setSelectedAccount(null);
      setSelectedBankAccountId(null);
      setSelectedBankAccount(null);
      setSelectedCashboxId(null);
      setSelectedCashbox(null);
      setCollectionMethod(getDefaultCollectionMethod(checkNote));
      setError(null);
    }
  }, [open, checkNote]);
  
  // Durum seçildiğinde tutarı güncelle
  useEffect(() => {
    if (checkNote) {
      if (selectedStatus === CheckNoteStatus.COLLECTED) {
        // Hem çekler hem senetler için kalan tutar gelsin, kullanıcı isterse değiştirir
        setAmount(checkNote.remainingAmount);
      } else if (selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED) {
        setAmount(0);
      }
    }
    // Durum değişince tahsilat yöntemini ve hesap seçimlerini sıfırla.
    // Bankaya gönderilmiş çekin tahsilatında varsayılan Banka + gönderilen hesap (alt hesap) seçili gelir.
    const collecting = selectedStatus === CheckNoteStatus.COLLECTED || selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED;
    setCollectionMethod(getDefaultCollectionMethod(checkNote));
    setSelectedCashboxId(null);
    setSelectedCashbox(null);
    setSelectedBankAccountId(collecting ? getSentBankOptionId(checkNote) : null);
    setSelectedBankAccount(null);
  }, [selectedStatus, checkNote]);

  // Önceden seçilen banka id'si için liste yüklenince seçenek nesnesini bul (Autocomplete değeri)
  useEffect(() => {
    if (selectedBankAccountId === null || selectedBankAccount) return;
    const match = bankAccounts.find(b => String(b.id) === String(selectedBankAccountId));
    if (match) setSelectedBankAccount(match);
  }, [bankAccounts, selectedBankAccountId, selectedBankAccount]);

  // Bankaya gönderilmiş çekte Kasa / Elden seçilirse gösterilecek uyarı metni
  const sentBankOptionId = getSentBankOptionId(checkNote);
  const sentBankOption = bankAccounts.find(b => String(b.id) === String(sentBankOptionId));
  const sentBankLabel = sentBankOption
    ? `${sentBankOption.bankName} - ${sentBankOption.accountName}`
    : (sentBankOptionId !== null ? `#${sentBankOptionId}` : '');
  const nonBankWarning = isCollectionStatus && shouldWarnNonBankCollection(checkNote, collectionMethod)
    ? t('checksNotes.statusChange.sentToBankWarning', {
        bank: sentBankLabel,
        method: collectionMethod === 'HAND'
          ? t('checksNotes.statusChange.methodHand')
          : t('checksNotes.statusChange.methodCashbox')
      })
    : null;
  
  // Kaydet
  const handleSave = async () => {
    if (!checkNote || !selectedStatus) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Validasyon
      if (selectedStatus === CheckNoteStatus.ENDORSED && !endorsedToAccountId) {
        setError(t('checksNotes.errors.endorsedAccountRequired'));
        setLoading(false);
        return;
      }
      
      // Tahsilat durumları (COLLECTED / PARTIAL_COLLECTED): yönteme göre kontrol
      if (isCollectionStatus) {
        if (collectionMethod === 'BANK' && !selectedBankAccountId) {
          setError(t('checksNotes.errors.bankAccountRequired'));
          setLoading(false);
          return;
        }
        if (collectionMethod === 'CASHBOX' && !selectedCashboxId) {
          setError(t('checksNotes.errors.cashboxRequired'));
          setLoading(false);
          return;
        }
        // HAND (elden teslim): hesap zorunlu değil, hareket oluşturulmaz
      } else if ((selectedStatus === CheckNoteStatus.COLLATERAL || 
           selectedStatus === CheckNoteStatus.IN_COLLECTION ||
           selectedStatus === CheckNoteStatus.GIVEN_PAID) && !selectedBankAccountId) {
        // Diğer banka gerektiren durumlar (teminat / tahsilde / verilen çek ödeme)
        setError(t('checksNotes.errors.bankAccountRequired'));
        setLoading(false);
        return;
      }
      
      if ((selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED || selectedStatus === CheckNoteStatus.COLLECTED) && amount <= 0) {
        setError(t('checksNotes.errors.amountRequired'));
        setLoading(false);
        return;
      }
      
      // Banka hesabı ID'sini ayıkla (alt hesap olabilir)
      let bankAccountId: number | undefined = undefined;
      let bankSubAccountId: number | undefined = undefined;
      let cashboxId: number | undefined = undefined;
      
      // Tahsilat durumunda yöntem (Kasa/Banka/Elden) belirleyici; diğer durumlarda banka.
      const useCashbox = isCollectionStatus && collectionMethod === 'CASHBOX';
      const useHand = isCollectionStatus && collectionMethod === 'HAND';
      
      if (useCashbox) {
        cashboxId = selectedCashboxId ?? undefined;
      } else if (!useHand && selectedBankAccountId) {
        // Banka (tahsilat durumunda BANK seçiliyken veya diğer banka durumlarında)
        const selectedBank = bankAccounts.find(b => String(b.id) === String(selectedBankAccountId));
        if (selectedBank?.isSubAccount && selectedBank.parentBankAccountId && selectedBank.subAccountId) {
          bankAccountId = selectedBank.parentBankAccountId;
          bankSubAccountId = selectedBank.subAccountId;
        } else {
          // "bankaId_altId" (liste henüz yüklenmemiş önseçim) veya ana hesap id'si
          ({ bankAccountId, bankSubAccountId } = parseBankOptionId(selectedBankAccountId));
        }
      }
      // useHand: ne banka ne kasa gönderilir → backend hareket oluşturmaz (elden teslim)
      
      const data: ChangeCheckNoteStatusDto = {
        id: checkNote.id,
        newStatus: selectedStatus,
        transactionDate,
        description: description || undefined,
        endorsedToAccountId: endorsedToAccountId || undefined,
        endorsedToAccountName: selectedAccount?.name || undefined,
        bankAccountId,
        bankSubAccountId,
        cashboxId,
        // Tahsilat yöntemi açıkça gönderilir (backend yöntemsiz tahsilatı reddeder)
        collectionMethod: isCollectionStatus ? collectionMethod : undefined,
        // Kısmi tahsil için tutar
        amount: (selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED || selectedStatus === CheckNoteStatus.COLLECTED) ? amount : undefined
      };
      
      // DEBUG: Kısmi tahsil için log
      if (selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED) {
        console.log('💰 [PARTIAL_COLLECTED] Kısmi tahsil gönderiliyor:', {
          checkNoteId: checkNote.id,
          amount: amount,
          remainingBefore: checkNote.remainingAmount,
          collectedBefore: checkNote.collectedAmount,
          data
        });
      }
      
      await changeCheckNoteStatus(data);
      onSuccess();
    } catch (err: any) {
      console.error('Durum değiştirme hatası:', err);
      const errorMessage = err.response?.data?.message || err.message || t('checksNotes.errors.statusChangeError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  if (!checkNote) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'grey.100'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6">{t('checksNotes.statusChange.title')}</Typography>
          <GuideButton guideKey="finance.changeCheckStatus" size="small" />
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Mevcut Durum */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{t('checksNotes.statusChange.document')}</Typography>
              <Typography variant="body1" fontWeight="bold">{checkNote.documentNumber}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{t('checksNotes.statusChange.currentStatus')}</Typography>
              <Box>
                <Chip 
                  label={getLocalizedStatusLabel(checkNote.status)} 
                  size="small"
                  color={getStatusColor(checkNote.status) as 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default'}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{t('checksNotes.table.amount')}</Typography>
              <Typography variant="body2">{formatCurrency(checkNote.amount, checkNote.currency)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.remainingAmount')}</Typography>
              <Typography variant="body2" color="error.main">{formatCurrency(checkNote.remainingAmount, checkNote.currency)}</Typography>
            </Grid>
          </Grid>
        </Box>
        
        {availableStatuses.length === 0 ? (
          <Alert severity="info">
            {t('checksNotes.statusChange.noAvailableStatus')}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {/* Yeni Durum Seçimi */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('checksNotes.statusChange.selectNewStatus')}</Typography>
              <Grid container spacing={1}>
                {availableStatuses.map((status) => (
                  <Grid item xs={6} md={4} key={status}>
                    <Button
                      fullWidth
                      variant={selectedStatus === status ? 'contained' : 'outlined'}
                      color={selectedStatus === status ? 'primary' : 'inherit'}
                      onClick={() => setSelectedStatus(status)}
                      sx={{ 
                        py: 1.5, 
                        flexDirection: 'column',
                        textTransform: 'none'
                      }}
                    >
                      {getStatusIcon(status)}
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        {getLocalizedStatusLabel(status)}
                      </Typography>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {/* Seçilen durum açıklaması */}
            {selectedStatus && (
              <Grid item xs={12}>
                <Alert severity="info" icon={false}>
                  {getStatusDescription(selectedStatus, t)}
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {/* İşlem Tarihi */}
            <Grid item xs={12} md={6}>
              <AppDatePicker
                fullWidth
                label={t('checksNotes.statusChange.transactionDate')}
                value={transactionDate}
                onChange={setTransactionDate}
                size="small"
              />
            </Grid>
            
            {/* Tutar (Tahsilat ve Kısmi Tahsilat için) */}
            {(selectedStatus === CheckNoteStatus.COLLECTED || selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED) && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.statusChange.collectionAmount')}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  size="small"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{checkNote.currency}</InputAdornment>,
                  }}
                  helperText={
                    selectedStatus === CheckNoteStatus.PARTIAL_COLLECTED 
                      ? t('checksNotes.statusChange.partialAmountHelp', { max: checkNote.remainingAmount })
                      : (checkNote.type === 'PROMISSORY_NOTE' 
                          ? t('checksNotes.statusChange.fullCollectionHelp', { amount: checkNote.remainingAmount.toFixed(2), currency: checkNote.currency })
                          : undefined)
                  }
                />
              </Grid>
            )}
            
            {/* Ciro Edilecek Cari (Ciro için) */}
            {selectedStatus === CheckNoteStatus.ENDORSED && (
              <Grid item xs={12}>
                <Autocomplete
                  options={accounts}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={selectedAccount}
                  onChange={(_, newValue) => {
                    setSelectedAccount(newValue);
                    setEndorsedToAccountId(newValue?.id || null);
                  }}
                  loading={loadingAccounts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('checksNotes.statusChange.endorsedToAccount')}
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            
            {/* Tahsilat Yöntemi (Kasa / Banka / Elden Teslim) */}
            {isCollectionStatus && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('checksNotes.statusChange.collectionMethod')}
                </Typography>
                <ToggleButtonGroup
                  value={collectionMethod}
                  exclusive
                  onChange={(_, value) => { if (value) setCollectionMethod(value as CollectionMethod); }}
                  fullWidth
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="CASHBOX" sx={{ textTransform: 'none', gap: 0.5 }}>
                    <MoneyIcon fontSize="small" /> {t('checksNotes.statusChange.methodCashbox')}
                  </ToggleButton>
                  <ToggleButton value="BANK" sx={{ textTransform: 'none', gap: 0.5 }}>
                    <AccountBalanceIcon fontSize="small" /> {t('checksNotes.statusChange.methodBank')}
                  </ToggleButton>
                  <ToggleButton value="HAND" sx={{ textTransform: 'none', gap: 0.5 }}>
                    <PanToolIcon fontSize="small" /> {t('checksNotes.statusChange.methodHand')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}

            {/* Bankaya gönderilmiş çekte Kasa / Elden seçildi uyarısı */}
            {nonBankWarning && (
              <Grid item xs={12}>
                <Alert severity="warning">{nonBankWarning}</Alert>
              </Grid>
            )}

            {/* Kasa Seçimi (tahsilat — Kasa yöntemi) */}
            {isCollectionStatus && collectionMethod === 'CASHBOX' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={cashboxes}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedCashbox}
                  onChange={(_, newValue) => {
                    setSelectedCashbox(newValue);
                    setSelectedCashboxId(newValue?.id || null);
                  }}
                  loading={loadingCashboxes}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('checksNotes.statusChange.cashbox')}
                      placeholder={t('checksNotes.statusChange.searchCashbox')}
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCashboxes ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2">{option.name}</Typography>
                        {typeof option.balance === 'number' && (
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(option.balance, checkNote.currency)}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Grid>
            )}

            {/* Elden Teslim bilgisi (hareket oluşturulmaz) */}
            {isCollectionStatus && collectionMethod === 'HAND' && (
              <Grid item xs={12}>
                <Alert severity="info" icon={false}>
                  {t('checksNotes.statusChange.handDeliveryInfo')}
                </Alert>
              </Grid>
            )}

            {/* Banka Hesabı */}
            {needsBankAccount && (!isCollectionStatus || collectionMethod === 'BANK') && (
              <Grid item xs={12}>
                <Autocomplete
                  options={bankAccounts}
                  groupBy={(option) => option.bankName}
                  getOptionLabel={(option) => option.accountName}
                  isOptionEqualToValue={(option, value) => 
                    String(option.id) === String(value.id)
                  }
                  value={selectedBankAccount}
                  onChange={(_, newValue) => {
                    setSelectedBankAccount(newValue);
                    setSelectedBankAccountId(newValue?.id || null);
                  }}
                  loading={loadingBankAccounts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('checksNotes.statusChange.bankAccount')}
                      placeholder={t('checksNotes.statusChange.searchBankAccount')}
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingBankAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={String(option.id)}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          pl: option.isSubAccount ? 2 : 0,
                          fontWeight: option.isSubAccount ? 'normal' : 600,
                          fontSize: option.isSubAccount ? '0.85rem' : '0.9rem'
                        }}
                      >
                        {option.isSubAccount ? '└ ' : ''}{option.accountName}
                      </Typography>
                    </li>
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <ListSubheader sx={{ bgcolor: 'grey.50', fontWeight: 600, lineHeight: '36px', fontSize: '0.85rem' }}>
                        <AccountBalanceIcon sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle', color: 'primary.main' }} />
                        {params.group}
                      </ListSubheader>
                      <ul style={{ padding: 0 }}>{params.children}</ul>
                    </li>
                  )}
                />
              </Grid>
            )}
            
            {/* Açıklama */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checksNotes.statusChange.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('checksNotes.form.cancel')}
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSave}
          disabled={loading || !selectedStatus || availableStatuses.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <SwapHorizIcon />}
        >
          {t('checksNotes.statusChange.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeStatusModal;
